/**
 * providerDetection.js
 * 
 * Reliable MetaMask/Web3 provider detection utility.
 * Uses official @metamask/detect-provider package to avoid race conditions
 * with provider injection timing.
 */

import detectEthereumProvider from '@metamask/detect-provider';

// Singleton cache for provider and ethers instances
let cachedProvider = null;
let cachedEthersProvider = null;
let cachedSigner = null;
let detectionPromise = null;

// Debug timeline for tracking provider operations
const debugTimeline = [];

function logTimeline(event, details = {}) {
  const entry = {
    timestamp: Date.now(),
    event,
    ...details
  };
  debugTimeline.push(entry);
  console.log(`[Provider Timeline] ${event}`, details);
}

/**
 * Detect MetaMask provider reliably using official detection.
 * Returns null if MetaMask is not installed or not available.
 * 
 * @param {Object} options
 * @param {boolean} options.mustBeMetaMask - If true, only returns MetaMask (not other wallets)
 * @param {boolean} options.silent - If true, don't log warnings
 * @param {number} options.timeout - Detection timeout in ms (default: 3000)
 * @returns {Promise<EthereumProvider|null>}
 */
export async function detectProvider({ 
  mustBeMetaMask = false, 
  silent = false,
  timeout = 3000 
} = {}) {
  logTimeline('detectProvider:start', { mustBeMetaMask, timeout });
  
  // Return cached if available and still valid
  if (cachedProvider && cachedProvider.isConnected?.()) {
    logTimeline('detectProvider:cached');
    return cachedProvider;
  }
  
  // Avoid concurrent detection calls
  if (detectionPromise) {
    logTimeline('detectProvider:waiting-existing');
    return detectionPromise;
  }
  
  detectionPromise = (async () => {
    try {
      const provider = await detectEthereumProvider({
        mustBeMetaMask,
        silent,
        timeout
      });
      
      if (!provider) {
        logTimeline('detectProvider:not-found');
        return null;
      }
      
      // Verify it's actually MetaMask if required
      if (mustBeMetaMask && !provider.isMetaMask) {
        logTimeline('detectProvider:not-metamask', { 
          isMetaMask: provider.isMetaMask 
        });
        return null;
      }
      
      cachedProvider = provider;
      logTimeline('detectProvider:success', { 
        isMetaMask: provider.isMetaMask,
        chainId: provider.chainId 
      });
      
      return provider;
    } catch (error) {
      logTimeline('detectProvider:error', { error: error.message });
      return null;
    } finally {
      detectionPromise = null;
    }
  })();
  
  return detectionPromise;
}

/**
 * Get ethers Web3Provider singleton.
 * Creates new instance only if provider has changed or doesn't exist.
 * 
 * @returns {Promise<{ethersProvider: Web3Provider, ethers: typeof import('ethers')}>}
 */
export async function getEthersProvider() {
  logTimeline('getEthersProvider:start');
  
  const ethereum = await detectProvider({ mustBeMetaMask: true });
  if (!ethereum) {
    logTimeline('getEthersProvider:no-provider');
    throw new Error('MetaMask not available');
  }
  
  // Import ethers lazily
  const { ethers } = await import('ethers');
  
  // Check if we need to create a new provider
  // This happens if: no cached provider, or the underlying ethereum changed
  if (!cachedEthersProvider || cachedEthersProvider.provider !== ethereum) {
    logTimeline('getEthersProvider:creating-new');
    cachedEthersProvider = new ethers.providers.Web3Provider(ethereum);
    cachedSigner = null; // Invalidate cached signer
  }
  
  logTimeline('getEthersProvider:success');
  return { ethersProvider: cachedEthersProvider, ethers };
}

/**
 * Get signer from cached ethers provider.
 * Validates signer is still connected to expected account.
 * 
 * @param {string} expectedAccount - Optional expected account address to validate
 * @returns {Promise<Signer>}
 */
export async function getSigner(expectedAccount = null) {
  logTimeline('getSigner:start', { expectedAccount });
  
  const { ethersProvider } = await getEthersProvider();
  
  // Get fresh signer. When an address is provided, use it directly so ethers
  // doesn't have to resolve index 0 from the accounts list — which can fail
  // immediately after wallet_revokePermissions + re-auth clears the cache.
  if (!cachedSigner) {
    cachedSigner = expectedAccount
      ? ethersProvider.getSigner(expectedAccount)
      : ethersProvider.getSigner();
  }

  // Validate signer matches expected account when specified
  if (expectedAccount) {
    try {
      const signerAddress = await cachedSigner.getAddress();
      if (signerAddress.toLowerCase() !== expectedAccount.toLowerCase()) {
        logTimeline('getSigner:account-mismatch', {
          expected: expectedAccount,
          actual: signerAddress
        });
        // Refresh provider and signer
        cachedEthersProvider = null;
        cachedSigner = null;
        return getSigner(expectedAccount);
      }
    } catch (error) {
      logTimeline('getSigner:validation-error', { error: error.message });
      throw error;
    }
  }
  
  logTimeline('getSigner:success');
  return cachedSigner;
}

/**
 * Clear all cached provider instances.
 * Call this on logout or when wallet changes.
 */
export function clearProviderCache() {
  logTimeline('clearProviderCache');
  cachedProvider = null;
  cachedEthersProvider = null;
  cachedSigner = null;
  detectionPromise = null;
}

/**
 * Check if MetaMask has a pending request.
 * MetaMask returns error -32002 when there's already a pending request.
 * 
 * @returns {Promise<boolean>}
 */
export async function hasPendingRequest() {
  const provider = await detectProvider({ mustBeMetaMask: true });
  if (!provider) return false;
  
  // MetaMask doesn't expose pending request state directly,
  // but we can track it via our mutex
  return false; // Caller should use walletActionMutex instead
}

/**
 * Get the debug timeline for troubleshooting.
 * @returns {Array} Timeline of provider operations
 */
export function getProviderTimeline() {
  return [...debugTimeline];
}

/**
 * Clear the debug timeline.
 */
export function clearProviderTimeline() {
  debugTimeline.length = 0;
}

export default {
  detectProvider,
  getEthersProvider,
  getSigner,
  clearProviderCache,
  hasPendingRequest,
  getProviderTimeline,
  clearProviderTimeline
};
